// ─── Validation Utilities ────────────────────────────────────────────────────

/** Email regex per spec */
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/** Common weak passwords to reject */
const COMMON_PASSWORDS = [
  'password', 'password1', 'password123', 'password1234',
  'qwerty', 'qwerty123', 'qwertyuiop',
  '123456', '1234567', '12345678', '123456789',
  'abc123', 'letmein', 'welcome', 'monkey',
  'dragon', 'master', 'sunshine', 'princess',
  'iloveyou', 'admin', 'login', 'pass',
  'test', 'test123', 'hello', 'hello123',
  'welcome1', 'football', 'baseball', 'shadow',
];

/**
 * Validate email format.
 * @param {string} email
 * @returns {boolean}
 */
export const validateEmail = (email) => {
  console.log('[validation.js] validateEmail called with:', email);
  if (!email || typeof email !== 'string') {
    console.log('[validation.js] validateEmail: early return false (empty or non-string)');
    return false;
  }
  try {
    console.log('[validation.js] validateEmail: About to call EMAIL_REGEX.test() — typeof EMAIL_REGEX:', typeof EMAIL_REGEX, '— value:', EMAIL_REGEX);
    const result = EMAIL_REGEX?.test(email?.trim());
    console.log('[validation.js] validateEmail: EMAIL_REGEX.test() result:', result);
    return result;
  } catch (err) {
    console.error('[validation.js] validateEmail: EMAIL_REGEX.test() THREW AN ERROR:', err?.name, err?.message, err?.stack);
    return false;
  }
};

/**
 * Password strength criteria.
 * Returns { score: 0-5, criteria: object, feedback: string[], isValid: boolean }
 */
export const validatePasswordStrength = (password) => {
  console.log('[validation.js] validatePasswordStrength called — password length:', password ? password?.length : 0);

  if (!password) {
    return { score: 0, criteria: {}, feedback: ['Password is required'], isValid: false };
  }

  const criteria = {};

  // length check (no regex)
  criteria.length = password?.length >= 8;
  console.log('[validation.js] validatePasswordStrength: length check result:', criteria?.length);

  // uppercase
  try {
    console.log('[validation.js] validatePasswordStrength: Testing uppercase regex /[A-Z]/');
    const UPPER_RE = /[A-Z]/;
    criteria.uppercase = UPPER_RE?.test(password);
    console.log('[validation.js] validatePasswordStrength: uppercase result:', criteria?.uppercase);
  } catch (err) {
    console.error('[validation.js] validatePasswordStrength: uppercase regex THREW:', err?.name, err?.message, err?.stack);
    criteria.uppercase = false;
  }

  // lowercase
  try {
    console.log('[validation.js] validatePasswordStrength: Testing lowercase regex /[a-z]/');
    const LOWER_RE = /[a-z]/;
    criteria.lowercase = LOWER_RE?.test(password);
    console.log('[validation.js] validatePasswordStrength: lowercase result:', criteria?.lowercase);
  } catch (err) {
    console.error('[validation.js] validatePasswordStrength: lowercase regex THREW:', err?.name, err?.message, err?.stack);
    criteria.lowercase = false;
  }

  // number
  try {
    console.log('[validation.js] validatePasswordStrength: Testing number regex /[0-9]/');
    const NUMBER_RE = /[0-9]/;
    criteria.number = NUMBER_RE?.test(password);
    console.log('[validation.js] validatePasswordStrength: number result:', criteria?.number);
  } catch (err) {
    console.error('[validation.js] validatePasswordStrength: number regex THREW:', err?.name, err?.message, err?.stack);
    criteria.number = false;
  }

  // special
  try {
    console.log('[validation.js] validatePasswordStrength: Testing special char regex');
    const SPECIAL_RE = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;
    criteria.special = SPECIAL_RE?.test(password);
    console.log('[validation.js] validatePasswordStrength: special result:', criteria?.special);
  } catch (err) {
    console.error('[validation.js] validatePasswordStrength: special regex THREW:', err?.name, err?.message, err?.stack);
    criteria.special = false;
  }

  const score = Object.values(criteria)?.filter(Boolean)?.length;
  const feedback = [];

  if (!criteria?.length) feedback?.push('At least 8 characters');
  if (!criteria?.uppercase) feedback?.push('At least 1 uppercase letter');
  if (!criteria?.lowercase) feedback?.push('At least 1 lowercase letter');
  if (!criteria?.number) feedback?.push('At least 1 number');
  if (!criteria?.special) feedback?.push('At least 1 special character (!@#$%^&*)');

  const isValid = score === 5;
  console.log('[validation.js] validatePasswordStrength: final score:', score, '— isValid:', isValid, '— criteria:', criteria);

  return { score, criteria, feedback, isValid };
};

/**
 * Check if password is in the common passwords list.
 * @param {string} password
 * @returns {boolean} true if too common
 */
export const isCommonPassword = (password) => {
  if (!password) return false;
  return COMMON_PASSWORDS?.includes(password?.toLowerCase()?.trim());
};

/**
 * Sanitize a numeric input — parse to number and clamp to [min, max].
 * @param {string|number} value
 * @param {number} min
 * @param {number} max
 * @returns {number|null}
 */
export const sanitizeNumericInput = (value, min = -Infinity, max = Infinity) => {
  const num = parseFloat(value);
  if (isNaN(num)) return null;
  return Math.min(Math.max(num, min), max);
};

/**
 * Format a number as currency with $ prefix and thousand separators.
 * @param {number|string} value
 * @returns {string}
 */
export const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '$0.00';
  return '$' + num?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Validate a value is within [min, max] range.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {boolean}
 */
export const validateRange = (value, min, max) => {
  const num = parseFloat(value);
  if (isNaN(num)) return false;
  return num >= min && num <= max;
};

/**
 * Get password strength label and colors based on score (0-5).
 */
export const getStrengthMeta = (score) => {
  if (score <= 1) return { label: 'Weak', barColor: 'bg-red-500', textColor: 'text-red-500', hexColor: '#ef4444', bars: 1 };
  if (score <= 2) return { label: 'Weak', barColor: 'bg-red-500', textColor: 'text-red-500', hexColor: '#ef4444', bars: 2 };
  if (score === 3) return { label: 'Medium', barColor: 'bg-yellow-500', textColor: 'text-yellow-500', hexColor: '#f59e0b', bars: 3 };
  if (score === 4) return { label: 'Medium', barColor: 'bg-yellow-500', textColor: 'text-yellow-500', hexColor: '#f59e0b', bars: 4 };
  return { label: 'Strong', barColor: 'bg-green-500', textColor: 'text-green-500', hexColor: '#22c55e', bars: 5 };
};
